import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  FormControl,
  FormLabel,
  TextField,
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Checkbox,
  Paper,
  Stack,
  RadioGroup,
  Radio,
  FormControlLabel,
  Typography,
  Button
} from '@mui/material';
import StarIcon from '@mui/icons-material/Star';
import { getPlaces, getPlacesNearby, sendTestEmail } from '../api/api';

const CrewNotificationSetup = ({ role, premium, token, domain, homeClub, selectedPlaces, setSelectedPlaces, notificationFrequency, setNotificationFrequency, messageNotifications, setMessageNotifications }) => {
  const { t } = useTranslation();
  const [distance, setDistance] = useState('25');
  const [nearbyPlaces, setNearbyPlaces] = useState([]);

  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 600);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  

  useEffect(() => {
    const loadNearbyPlaces = async () => {
      if (homeClub && distance) {
        try {
         // const places = await getPlacesNearby(token, homeClub, parseInt(distance, 10));
          const places = (await getPlaces(domain)).filter(x=>x._id !== homeClub);
          
          places.sort((a, b) => a.name.localeCompare(b.name));
          setNearbyPlaces(places);
        } catch (error) {
          console.error('Error loading nearby places:', error);
        }
      }
    };
    loadNearbyPlaces();
  }, [token, homeClub, domain, distance]);

  const handlePlaceToggle = (placeId) => () => {
    const currentIndex = selectedPlaces.indexOf(placeId);
    const newSelectedPlaces = [...selectedPlaces];

    if (currentIndex === -1) {
      // Check if adding would exceed the limit of 20 places
      if (newSelectedPlaces.length < 20) {
        newSelectedPlaces.push(placeId);
      }
    } else {
      newSelectedPlaces.splice(currentIndex, 1);
    }

    setSelectedPlaces(newSelectedPlaces);
  };

  const [testEmailSent, setTestEmailSent] = useState(false);
  const [testEmailSending, setTestEmailSending] = useState(false);
  const [testEmailError, setTestEmailError] = useState(false);

  const handleTestEmail = async () => {
    try {
      setTestEmailSent(false);
      setTestEmailError(false);
      setTestEmailSending(true);
      // Call the actual API endpoint to send a test email
      await sendTestEmail(token);
        setTestEmailSent(true);
        setTestEmailError(false);
      
        // Reset the success message after 3 seconds
        setTimeout(() => {
            setTestEmailSent(false);
        }, 3000);
            
    } catch (error) {
      console.error('Error sending test email:', error);
      setTestEmailError(true);
      setTestEmailSent(false);
      
      // Clear error message after 3 seconds
      setTimeout(() => {
        setTestEmailError(false);
      }, 3000);
    }
    finally
    {
      setTestEmailSending(false);
    }
  };

  return (
    <Box sx={{ width: isMobile ? '100%' : '90%', textAlign: 'left' }}>
    <FormControl component="fieldset" margin="">
        <FormLabel component="legend" sx={{ mb: 1 }}>
          {role === 'crew' 
            ? t('profile.notifications.preferenceCrew')
            : t('profile.notifications.preferenceSkipper')
          }
        </FormLabel>
        <RadioGroup
          row = {premium && role === 'crew'}
          value={notificationFrequency}
          onChange={(e) => setNotificationFrequency(e.target.value)}
        >
          <FormControlLabel 
            value="immediate" 
            control={<Radio />} 
            label={t('profile.notifications.options.immediate')} 
          />
          <FormControlLabel 
            value="daily" 
            control={<Radio />} 
            label={t('profile.notifications.options.daily')} 
          />
          <FormControlLabel 
            value="never" 
            control={<Radio />} 
            label={t('profile.notifications.options.never')} 
          />
        </RadioGroup>
      </FormControl>

      <Box sx={{ mt: 1 }}>
        <FormControlLabel
          control={
            <Checkbox
              checked={Boolean(messageNotifications)}
              onChange={(e) => setMessageNotifications(e.target.checked)}
            />
          }
          label={t('profile.notifications.unreadMessages')}
        />
      </Box>

      {notificationFrequency !== 'never' && (
        <Box sx={{ mt: 2, mb: 2 }}>
          {/* <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {t('profile.notifications.testEmailExplanation')}
          </Typography> */}
          <Button 
            variant="outlined" 
            color={testEmailError ? "error" : "primary"} 
            onClick={handleTestEmail}
            disabled={testEmailSending}
          >
            { testEmailSending 
              ? 'Sending...'
              : testEmailSent 
                ? 'Email Sent!' 
                : testEmailError 
                  ? 'Error sending email' 
                  : t('profile.notifications.testEmail')
            }
          </Button>
        </Box>
      )}

      {role === 'crew' && notificationFrequency !== 'never' &&
      ( premium ? 
        <FormControl component="fieldset" margin="normal" sx={{ width: '100%' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <StarIcon sx={{ color: '#FFD700', ml: "-4px", mr: 1, fontSize: '1.7rem',        filter: 'drop-shadow(1px 1px 1px rgb(78, 47, 9))' } } />
            <FormLabel component="legend">
              {t('profile.notifications.clubsLabel')}
            </FormLabel>
          </Box>
          
          <Paper variant="outlined" sx={{ maxHeight: 200, overflow: 'auto' }}>
            <List sx={{ width: '100%', bgcolor: 'background.paper' }}>
              {nearbyPlaces.map((place) => {
                const labelId = `checkbox-list-label-${place._id}`;

                return (
                  <ListItem
                    key={place._id}
                    dense
                    button
                    onClick={handlePlaceToggle(place._id)}
                  >
                    <ListItemIcon>
                      <Checkbox
                        edge="start"
                        checked={selectedPlaces.includes(place._id)}
                        tabIndex={-1}
                        disableRipple
                        inputProps={{ 'aria-labelledby': labelId }}
                      />
                    </ListItemIcon>
                    <ListItemText id={labelId} primary={place.name} />
                  </ListItem>
                );
              })}
            </List>
          </Paper>
        </FormControl>
      :
     
        <Box sx={{ mt: 2 }}>
          {/* <Typography color="text.secondary">
            {t('profile.notifications.upgradeMessage', 'Upgrade to premium to receive notifications about sailing opportunities in nearby clubs!')}
          </Typography> */}
        </Box>
      )
      }

     
    </Box>
  );
};

export default CrewNotificationSetup;
